// SEND-215 (Howard ruled 2026-08-14) — THE WALK LOOKS LIKE THE OLD TWO-PANE
// ANNOTATE SCREEN. "Photo Takeoff is hard to see what to do. Photo LEFT,
// current step's tools RIGHT."
//
// THIS PANE IS THE RIGHT HALF, AND IT SHOWS THIS STEP ONLY: step N of N, the
// step name, ONE short instruction, that step's tools, BACK · NEXT — FINISHED
// instead of NEXT on the last step — and EXIT GUIDED MODE. SKIP stays as a
// smaller control underneath, because it is the only thing that RECORDS a
// step that does not apply; without it the finished list could no longer say
// "nothing was drawn for FASCIA".
//
// IT DRAWS, MEASURES, STORES AND PRICES NOTHING. The step's tools are handed
// in as children by the editor — the SAME controls the free editor uses, not
// copies — so a control can never drift between the two views. FINISHED and
// EXIT both leave the walk and land on the grouped editor.
import React from "react";
import { ChevronLeft, ChevronRight, Check, LogOut, SkipForward } from "lucide-react";

export const WalkPane = ({
  steps, stepIdx, navBlock, onBack, onNext, onSkip, onFinish, onExit,
  children,
}) => {
  const step = steps[stepIdx] || steps[0];
  const last = stepIdx >= steps.length - 1;
  const held = !!navBlock;
  return (
    <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-[var(--border)] flex flex-col min-h-0 bg-[var(--surface)]"
      data-testid="photo-takeoff-walk-pane">
      <div className="px-3 py-2 border-b border-[var(--border)] bg-[var(--surface-muted)]">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]"
          data-testid="photo-takeoff-walk-progress">
          step {stepIdx + 1} of {steps.length}
        </div>
        <div className="text-base font-bold leading-tight mt-0.5"
          data-testid="photo-takeoff-walk-step-title">
          {step.title}
        </div>
        <div className="text-[11px] text-[var(--ink-2)] mt-1"
          data-testid="photo-takeoff-walk-step-short">
          {step.short}
        </div>
      </div>

      <div className="p-3 flex-1 overflow-y-auto min-h-0"
        data-testid="photo-takeoff-walk-step-tools">
        {children}
      </div>

      <div className="p-3 border-t border-[var(--border)] flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <button type="button" onClick={onBack} disabled={held || stepIdx === 0}
            title={navBlock || "back one step — nothing you drew is undone"}
            className="flex-1 px-2 py-2 border border-[var(--border)] text-[11px] font-bold uppercase tracking-wider disabled:opacity-40 flex items-center justify-center gap-1 hover:bg-[var(--surface-muted)]"
            data-testid="photo-takeoff-walk-back">
            <ChevronLeft size={13} /> back
          </button>
          {last ? (
            <button type="button" onClick={onFinish} disabled={held}
              title={navBlock || "finished — this drops you back on the grouped tool list"}
              className="flex-1 px-2 py-2 bg-[var(--success)] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-40 flex items-center justify-center gap-1"
              data-testid="photo-takeoff-walk-finish">
              <Check size={13} /> finished
            </button>
          ) : (
            <button type="button" onClick={onNext} disabled={held}
              title={navBlock || "next step — the tool changes, your marks stay"}
              className="flex-1 px-2 py-2 bg-[var(--ai)] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-40 flex items-center justify-center gap-1"
              data-testid="photo-takeoff-walk-next">
              next <ChevronRight size={13} />
            </button>
          )}
        </div>
        <button type="button" onClick={onSkip} disabled={held}
          title={navBlock || "this step does not apply on this photo — it is recorded as skipped and named at the end"}
          className="px-2 py-1 border border-[var(--border)] text-[10px] uppercase tracking-wider text-[var(--muted)] disabled:opacity-40 flex items-center justify-center gap-1 hover:bg-[var(--surface-muted)]"
          data-testid="photo-takeoff-walk-skip">
          <SkipForward size={11} /> skip this step
        </button>
        <button type="button" onClick={onExit}
          title="leave the walk — the grouped tool list comes back and nothing you drew is touched"
          className="px-2 py-1 border border-[var(--border)] text-[10px] font-bold uppercase tracking-wider text-[var(--ink-2)] flex items-center justify-center gap-1 hover:bg-[var(--surface-muted)]"
          data-testid="photo-takeoff-walk-exit">
          <LogOut size={11} /> exit guided mode
        </button>
        <div className={held
          ? "text-[10px] font-bold text-[var(--warning-text)] leading-snug"
          : "text-[9px] text-[var(--muted)] leading-snug"}
          data-testid="photo-takeoff-walk-note">
          {navBlock || "finished and exit both land on the grouped tool list"}
        </div>
      </div>
    </div>
  );
};

export default WalkPane;
